import React from 'react';
import { ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const BSEarlyEd = () => {
  const coreCourses = [
    { code: 'EDU 101', name: 'Introduction to Education', credits: 3 },
    { code: 'EDU 120', name: 'Nutrition in Early Childhood', credits: 3 },
    { code: 'EDU 124', name: 'Creative Activities 1', credits: 3 },
    { code: 'EDU 125', name: 'Creative Activities 2', credits: 3 },
    { code: 'EDU 142', name: 'Introduction to Special Education', credits: 3 },
    { code: 'EDU 210', name: 'Early Language and Literacy 1', credits: 3 },
    { code: 'EDU 211', name: 'Early Language and Literacy 2', credits: 3 },
    { code: 'EDU 212', name: 'Cognitive Development', credits: 3 },
    { code: 'EDU 215', name: 'Children with Special Needs', credits: 3 },
    { code: 'EDU 301', name: 'Creating Instructional Materials', credits: 3 },
    { code: 'EDU 324', name: 'Introduction to Administrative Preschool Centers', credits: 3 },
    { code: 'EDU 419', name: 'Education Psychology', credits: 3 },
    { code: 'EDU 499', name: 'ECE Internship', credits: 18 }
  ];

  const electiveCourses = [
    { code: 'BBA 300', name: 'Reviews of Management and Leadership Principles 1', credits: 3 },
    { code: 'BBA 301', name: 'Review of Management and Leadership Principles 2', credits: 3 },
    { code: 'EDU 323', name: 'Human Resource Management in ECE', credits: 3 },
    { code: 'EDU 434', name: 'Management of Information Systems', credits: 3 },
    { code: 'EDU 436', name: 'Research Methods in ECE 1', credits: 3 },
    { code: 'EDU 437', name: 'Research Methods in ECE 2', credits: 3 },
    { code: 'EDU 484', name: 'Ethics and Honesty in Education', credits: 3 },
    { code: 'EDU 489', name: 'Introduction to School Laws', credits: 3 },
    { code: 'EDU 497', name: 'Directed Reading 1', credits: 3 },
    { code: 'EDU 498', name: 'Directed Reading 2', credits: 3 }
  ];

  const programFeatures = [
    {
      title: "Comprehensive Curriculum",
      description: "Balanced blend of theory and practical experience in early childhood education",
      icon: "📚"
    },
    {
      title: "Hands-on Experience",
      description: "Extensive internship opportunities in real educational settings",
      icon: "👥"
    },
    {
      title: "Specialized Training",
      description: "Focus on special education and diverse learning needs",
      icon: "🎯"
    },
    {
      title: "Leadership Development",
      description: "Management and administration skills for educational settings",
      icon: "⭐"
    }
  ];

  const resources = [
    {
      title: "Program Handbook",
      description: "Complete guide to program requirements and policies",
      path: "/academics/bs_early_ed/handbook"
    },
    {
      title: "Internship Opportunities",
      description: "Information about field experience and placement",
      path: "/academics/bs_early_ed/internships"
    },
    {
      title: "Career Paths",
      description: "Explore potential career opportunities in early childhood education",
      path: "/academics/bs_early_ed/careers"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Bachelor of Science in Early Childhood Education
            </h1>
            <p className="text-xl opacity-90">
              Preparing tomorrow's educators for excellence in early childhood development
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                Our Bachelor of Science in Early Childhood Education program prepares you for a 
                rewarding career in teaching and childcare. Learn more about our{' '}
                <InlineLink to="/academics/bs_early_ed/curriculum">curriculum structure</InlineLink> and{' '}
                <InlineLink to="/academics/bs_early_ed/outcomes">learning outcomes</InlineLink>.
              </p>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {programFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Core Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Core Courses (54 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {coreCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Elective Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Educational and Administrative Electives (12 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {electiveCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Resources Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Resources</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {resources.map((resource, index) => (
                  <ResourceLink
                    key={index}
                    to={resource.path}
                    title={resource.title}
                    description={resource.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Start Your Teaching Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Take the first step towards becoming an early childhood educator.
                Join our community of dedicated education professionals.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default BSEarlyEd; 